"use client";

import { c as _c } from "react/compiler-runtime";
import { formatVoiceDuration, formatVoiceSessionStartedAt, type MagnusUIMessage, VOICE_DELEGATION_STATUS_LABEL } from "@bg-bgi/magnus-sdk";
import { Button } from "@workspace/ui/components/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@workspace/ui/components/dialog";
import { cn } from "@workspace/ui/lib/utils";
import { AudioLinesIcon, CornerDownRightIcon, ThumbsDownIcon, ThumbsUpIcon } from "lucide-react";
import { useState } from "react";
import type { MessageFeedbackProps } from "./message-actions";
type MessagePart = MagnusUIMessage["parts"][number];
export type VoiceSessionPartData = Extract<MessagePart, {
  type: "data-voice-session";
}>["data"];
type Delegation = NonNullable<VoiceSessionPartData["turns"][number]["delegations"]>[number];
const DELEGATION_TONE: Record<Delegation["status"], "done" | "failed" | "noted"> = {
  completed: "done",
  failed: "failed",
  "timed-out": "noted",
  interrupted: "noted",
  "awaiting-approval": "noted"
};
export function isVoiceSessionPart(part: MessagePart): part is Extract<MessagePart, {
  type: "data-voice-session";
}> {
  return part.type === "data-voice-session";
}
function VoteButton(t0) {
  const $ = _c(15);
  const {
    direction,
    feedback
  } = t0;
  const voted = feedback.vote === direction;
  const Icon = direction === "up" ? ThumbsUpIcon : ThumbsDownIcon;
  const t1 = voted ? "Thank you for your feedback" : `${direction === "up" ? "Good" : "Bad"} voice chat`;
  const t2 = voted && "text-foreground";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("size-7 rounded-full", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== direction || $[3] !== feedback) {
    t4 = () => feedback.vote === undefined && feedback.onVote(direction);
    $[2] = direction;
    $[3] = feedback;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = voted && (direction === "up" ? "text-success" : "text-destructive");
  let t6;
  if ($[5] !== t5) {
    t6 = cn("size-3.5", t5);
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== Icon || $[8] !== t6) {
    t7 = <Icon className={t6} />;
    $[7] = Icon;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t1 || $[11] !== t3 || $[12] !== t4 || $[13] !== t7) {
    t8 = <Button aria-label={t1} className={t3} onClick={t4} size="icon" type="button" variant="ghost">{t7}</Button>;
    $[10] = t1;
    $[11] = t3;
    $[12] = t4;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
}
export function VoiceDelegationRow(t0) {
  const $ = _c(23);
  const {
    className,
    delegation
  } = t0;
  const label = VOICE_DELEGATION_STATUS_LABEL[delegation.status];
  const tone = DELEGATION_TONE[delegation.status];
  const retried = (delegation.attempts ?? 1) > 1;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex h-6 w-full min-w-0 items-center gap-2 text-xs leading-none", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = delegation.query;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <CornerDownRightIcon className="size-3 shrink-0 text-muted-foreground/70" />;
    t4 = <span className="shrink-0 text-muted-foreground">Magnus</span>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== delegation.query) {
    t5 = <span className="min-w-16 truncate text-foreground/85">{delegation.query}</span>;
    $[4] = delegation.query;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== retried) {
    t6 = retried && <span className="text-muted-foreground/70">retried ·</span>;
    $[6] = retried;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = tone === "done" && "bg-success/80";
  const t8 = tone === "failed" && "bg-transparent ring-1 ring-destructive/70 ring-inset";
  const t9 = tone === "noted" && "bg-muted-foreground/50";
  let t10;
  if ($[8] !== t7 || $[9] !== t8 || $[10] !== t9) {
    t10 = cn("size-1.5 shrink-0 rounded-full", t7, t8, t9);
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10) {
    t11 = <span aria-hidden={true} className={t10} />;
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== label || $[15] !== t11 || $[16] !== t6) {
    t12 = <span className="ml-auto flex shrink-0 items-center gap-1.5 pl-2 text-muted-foreground">{t6}{label}{t11}</span>;
    $[14] = label;
    $[15] = t11;
    $[16] = t6;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== delegation.query || $[19] !== t1 || $[20] !== t12 || $[21] !== t5) {
    t13 = <div className={t1} title={t2}>{t3}{t4}{t5}{t12}</div>;
    $[18] = delegation.query;
    $[19] = t1;
    $[20] = t12;
    $[21] = t5;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  return t13;
}
export function VoiceTranscriptDialog(t0) {
  const $ = _c(24);
  const {
    data,
    onOpenChange,
    open
  } = t0;
  let t1;
  if ($[0] !== data.startedAt) {
    t1 = formatVoiceSessionStartedAt(data.startedAt);
    $[0] = data.startedAt;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const when = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DialogTitle className="flex items-center gap-2"><AudioLinesIcon className="size-4 text-muted-foreground" />Voice chat</DialogTitle>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== data.durationMs) {
    t3 = formatVoiceDuration(data.durationMs);
    $[3] = data.durationMs;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3 || $[6] !== when) {
    t4 = [when, t3].filter(Boolean);
    $[5] = t3;
    $[6] = when;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = t4.join(" \xB7 ");
  let t6;
  if ($[8] !== t5) {
    t6 = <DialogHeader>{t2}<DialogDescription>{t5}</DialogDescription></DialogHeader>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== data.summary) {
    t7 = data.summary && <p className="text-muted-foreground text-sm leading-relaxed">{data.summary}</p>;
    $[10] = data.summary;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== data.turns) {
    t8 = data.turns.map(_temp);
    $[12] = data.turns;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <ol className="-mx-6 flex max-h-[60vh] flex-col gap-3 overflow-y-auto px-6">{t8}</ol>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t6 || $[17] !== t7 || $[18] !== t9) {
    t10 = <DialogContent className="sm:max-w-lg">{t6}{t7}{t9}</DialogContent>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== onOpenChange || $[21] !== open || $[22] !== t10) {
    t11 = <Dialog onOpenChange={onOpenChange} open={open}>{t10}</Dialog>;
    $[20] = onOpenChange;
    $[21] = open;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
}
function _temp(turn, index) {
  return <li className={cn("flex flex-col gap-1", turn.role === "user" && "items-end")} key={`${turn.at}-${index}`}><span className="text-muted-foreground text-xs">{turn.role === "user" ? "You" : "Magnus"}</span><p className={cn("wrap-anywhere whitespace-pre-wrap text-sm leading-relaxed", turn.role === "user" && "w-fit max-w-[85%] rounded-lg bg-secondary px-3 py-2")}>{turn.text}</p>{turn.delegations?.map((delegation, i) => <VoiceDelegationRow className={turn.role === "user" ? "max-w-[85%]" : undefined} delegation={delegation} key={i} />)}</li>;
}
export function VoiceSessionChip(t0) {
  const $ = _c(27);
  const {
    data,
    feedback
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div aria-hidden={true} className="h-px flex-1 bg-border" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== feedback) {
    t2 = feedback && <div aria-hidden={true} className="w-14" />;
    $[1] = feedback;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setOpen(true);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = data.summary;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <AudioLinesIcon className="size-3.5" />;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== data.durationMs) {
    t6 = formatVoiceDuration(data.durationMs);
    $[5] = data.durationMs;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t6) {
    t7 = <span>Voice chat ·{" "}<span className="tabular-nums">{t6}</span></span>;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== data.summary || $[10] !== t7) {
    t8 = <Button aria-label="Open voice chat transcript" className="h-7 gap-1.5 rounded-full px-2.5 font-normal text-muted-foreground text-xs hover:text-foreground" onClick={t3} size="sm" title={t4} type="button" variant="ghost">{t5}{t7}</Button>;
    $[9] = data.summary;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== feedback) {
    t9 = feedback && <div className={cn("flex w-14 items-center transition-opacity duration-150 motion-reduce:transition-none", feedback.vote === undefined && "opacity-0 group-focus-within/voice-divider:opacity-100 group-hover/voice-divider:opacity-100 [@media(hover:none)]:opacity-100")}><VoteButton direction="up" feedback={feedback} /><VoteButton direction="down" feedback={feedback} /></div>;
    $[12] = feedback;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t2 || $[15] !== t8 || $[16] !== t9) {
    t10 = <div className="flex items-center gap-0.5">{t2}{t8}{t9}</div>;
    $[14] = t2;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div aria-hidden={true} className="h-px flex-1 bg-border" />;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10) {
    t12 = <div className="group/voice-divider flex w-full items-center gap-3 py-1 text-muted-foreground">{t1}{t10}{t11}</div>;
    $[19] = t10;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== data || $[22] !== open) {
    t13 = <VoiceTranscriptDialog data={data} onOpenChange={setOpen} open={open} />;
    $[21] = data;
    $[22] = open;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t12 || $[25] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  return t14;
}